import { useState, useEffect } from 'react';
import { Task } from '@/types/tasks';
import { useTasks } from '@/contexts/task/TaskContext';
import { toast } from '@/hooks/errors/use-toast';
import { useTaskContextTracking } from '@/hooks/tasks/useTaskContextTracking';

interface UseTaskCardStateProps {
  task: Task;
  onUpdateTask?: (task: Task) => void;
  // NEW: Optional custom task operations for goal-specific contexts
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
}

export const useTaskCardState = ({ task, onUpdateTask, customTaskOperations }: UseTaskCardStateProps) => {
  const [isCompleted, setIsCompleted] = useState(task.isCompleted || false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Use context tracking for task changes
  const { trackTaskChange, canPerformOperation } = useTaskContextTracking();
  
  // Use custom operations if provided, otherwise fall back to global context
  const globalTaskOperations = useTasks();
  const taskOperations = customTaskOperations || globalTaskOperations;
  
  const { completeTask, uncompleteTask, removeTask, updateTask } = taskOperations;

  // Handle checkbox change with context tracking
  const handleCheckboxChange = async (checked: boolean) => {
    // Check if we can perform the operation
    if (!canPerformOperation('complete')) {
      toast.info("Please wait for the current operation to complete");
      return;
    }
    
    setIsCompleted(checked);
    
    // Track the completion change for context
    if (task.goalId) { // Only track if task is associated with a goal
      await trackTaskChange(task.id, 'complete');
    }
    
    if (checked) {
      completeTask?.(task.id);
      // Toast notification removed for completing tasks
    } else {
      uncompleteTask?.(task.id);
    }
    
    // Stop propagation is handled in the component
  };

  // Handle card click to open the edit modal
  const handleCardClick = () => {
    setIsEditModalOpen(true);
  };

  // Stop propagation to allow clicking on buttons without triggering card click
  const handleClickStopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleEditTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditModalOpen(true);
  };

  const handleUpdateTask = (taskId: string, updatedTask: Partial<Task>, silent: boolean = false) => {
    // Pass the silent parameter to updateTask
    updateTask?.(taskId, updatedTask, silent);
    
    if (onUpdateTask && task) {
      onUpdateTask({ ...task, ...updatedTask });
    }
  };

  const handleDeleteTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Start delete animation
    setIsDeleting(true);
    
    // Short delay before actually removing from state
    setTimeout(() => {
      removeTask?.(task.id);
    }, 300); // Match animation duration
  };

  useEffect(() => {
    setIsCompleted(task.isCompleted || false);
  }, [task.isCompleted]);

  return {
    isCompleted,
    isEditModalOpen,
    setIsEditModalOpen,
    isDeleting,
    handleCheckboxChange,
    handleCardClick,
    handleClickStopPropagation,
    handleEditTask,
    handleUpdateTask,
    handleDeleteTask
  };
};

export default useTaskCardState;
